import type {
  AddressResolutionReason,
  AddressResolutionResult,
} from "../hooks/use-resolve-delivery-address";

export type CreateDeliveryOrderInput = {
  orgSlug: string;
  customerAddressId?: string;
  deliveryAddress?: {
    label?: string | null;
    line1: string;
    line2?: string | null;
    city?: string | null;
    country?: string;
    lat?: number | null;
    lng?: number | null;
    notes?: string | null;
  };
  addressResolutionToken?: string;
  paymentMode: "pay_now" | "pay_later";
  items: Array<{ productId: string; quantity: number; notes?: string | null }>;
  contactName?: string | null;
  contactPhone?: string | null;
  notes?: string | null;
  saveAsCustomerAddress?: boolean;
  customerAddressLabel?: string;
};

export type CreateDeliveryOrderResponse = {
  order: {
    id: string;
    dailyNumber: number;
    dailyDate: string;
    status: string;
    paymentStatus: string;
    paymentMode: "pay_now" | "pay_later";
    fulfillmentType: "delivery";
    currency: string;
    subtotal: number;
    discountTotal: number;
    deliveryFee: number;
    total: number;
  };
  checkoutUrl: string | null;
};

export type DeliveryCheckoutPaymentMode = "pay_now" | "pay_later";

export type DeliveryCheckoutParticipant =
  | { kind: "guest" }
  | { kind: "consumer"; emailVerified: boolean; phoneVerified: boolean }
  | { kind: "restaurant_account" };

export type DeliveryCheckoutAddressSelection =
  | { kind: "inline" }
  | { kind: "saved"; customerAddressId: string };

export type DeliveryCheckoutCartSnapshot = {
  lines: Array<{ productId: string; quantity: number }>;
  itemCount: number;
};

export type DeliveryCheckoutPricingSnapshot = {
  subtotal: number;
  deliveryMinimumOrder: number;
  deliveryFee: number;
};

export type DeliveryCheckoutBlockingReason =
  | "restaurant_account"
  | "empty_cart"
  | "minimum_order_not_met"
  | "map_unavailable"
  | "address_resolution_required"
  | "payment_unavailable"
  | "submission_pending"
  | { kind: "address_resolution"; code: AddressResolutionReason };

export type DeliveryCheckoutBrowserLocationReason =
  | "geolocation_denied"
  | "geolocation_unavailable"
  | "geolocation_timeout"
  | "geolocation_unsupported";

export type DeliveryCheckoutAddressFeedbackReason =
  | AddressResolutionReason
  | DeliveryCheckoutBrowserLocationReason
  | "map_unavailable";

export type DeliveryCheckoutPayLaterUnavailableReason =
  | "not_offered"
  | "guest_requires_sign_in"
  | "restaurant_account"
  | "email_verification_required"
  | "phone_verification_required";

export type DeliveryCheckoutAddress = {
  label: string;
  line1: string;
  line2: string;
  city: string;
  country: string;
  notes: string;
  lat: number | null;
  lng: number | null;
};

export type DeliveryCheckoutState = {
  paymentMode: DeliveryCheckoutPaymentMode;
  addressSelection: DeliveryCheckoutAddressSelection;
  deliveryAddress: DeliveryCheckoutAddress;
  contactName: string;
  contactPhone: string;
  notes: string;
  resolution: AddressResolutionResult | null;
  mapUnavailable: boolean;
  browserLocationReason: DeliveryCheckoutBrowserLocationReason | null;
  saveAsCustomerAddress: boolean;
  customerAddressLabel: string;
  isGeocoding: boolean;
  resolutionPending: boolean;
  activeResolutionRequestId: number | null;
  error: unknown | null;
};

type AddressField = keyof DeliveryCheckoutAddress;
type TextField = "contactName" | "contactPhone" | "notes" | "customerAddressLabel";

export type DeliveryCheckoutAction =
  | {
      type: "address-field-changed";
      field: AddressField;
      value: DeliveryCheckoutAddress[AddressField];
    }
  | { type: "text-field-changed"; field: TextField; value: string }
  | { type: "payment-mode-changed"; paymentMode: DeliveryCheckoutPaymentMode }
  | { type: "address-selection-changed"; selection: DeliveryCheckoutAddressSelection }
  | { type: "save-address-changed"; value: boolean }
  | { type: "address-replaced"; address: DeliveryCheckoutAddress }
  | { type: "map-pin-changed"; lat: number; lng: number }
  | { type: "map-load-failed" }
  | {
      type: "browser-location-reason-changed";
      reason: DeliveryCheckoutBrowserLocationReason | null;
    }
  | { type: "geocoding-started" }
  | {
      type: "geocoding-completed";
      address: Pick<DeliveryCheckoutAddress, "line1" | "line2" | "city" | "country">;
    }
  | { type: "geocoding-failed" }
  | { type: "resolution-cleared" }
  | { type: "error-recorded"; error: unknown }
  | { type: "error-cleared" }
  | { type: "resolution-started"; requestId: number }
  | {
      type: "resolution-completed";
      requestId: number;
      resolution: AddressResolutionResult;
    }
  | { type: "resolution-failed"; requestId: number };

const CANONICAL_ADDRESS_FIELDS = new Set<AddressField>(["line1", "city", "country", "lat", "lng"]);

export function isCanonicalDeliveryAddressField(field: AddressField): boolean {
  return CANONICAL_ADDRESS_FIELDS.has(field);
}

export function createInitialDeliveryCheckoutState(
  selection: DeliveryCheckoutAddressSelection = { kind: "inline" },
): DeliveryCheckoutState {
  return {
    activeResolutionRequestId: null,
    browserLocationReason: null,
    contactName: "",
    contactPhone: "",
    customerAddressLabel: "",
    deliveryAddress: {
      city: "",
      country: "UY",
      label: "",
      lat: null,
      line1: "",
      line2: "",
      lng: null,
      notes: "",
    },
    error: null,
    isGeocoding: false,
    mapUnavailable: false,
    notes: "",
    paymentMode: "pay_now",
    resolution: null,
    resolutionPending: false,
    saveAsCustomerAddress: false,
    addressSelection: selection,
  };
}

export function deliveryCheckoutReducer(
  state: DeliveryCheckoutState,
  action: DeliveryCheckoutAction,
): DeliveryCheckoutState {
  switch (action.type) {
    case "address-field-changed": {
      const invalidatesResolution = isCanonicalDeliveryAddressField(action.field);
      return {
        ...state,
        activeResolutionRequestId: invalidatesResolution ? null : state.activeResolutionRequestId,
        deliveryAddress: {
          ...state.deliveryAddress,
          [action.field]: action.value,
        },
        resolution: invalidatesResolution ? null : state.resolution,
        resolutionPending: invalidatesResolution ? false : state.resolutionPending,
      };
    }
    case "text-field-changed":
      return { ...state, [action.field]: action.value };
    case "payment-mode-changed":
      return { ...state, paymentMode: action.paymentMode };
    case "address-selection-changed":
      return {
        ...state,
        activeResolutionRequestId: null,
        resolution: null,
        resolutionPending: false,
        addressSelection: action.selection,
      };
    case "save-address-changed":
      return { ...state, saveAsCustomerAddress: action.value };
    case "address-replaced":
      return {
        ...state,
        activeResolutionRequestId: null,
        deliveryAddress: action.address,
        resolution: null,
        resolutionPending: false,
      };
    case "map-pin-changed":
      return {
        ...state,
        browserLocationReason: null,
        deliveryAddress: {
          ...state.deliveryAddress,
          lat: action.lat,
          lng: action.lng,
        },
        mapUnavailable: false,
        resolution: null,
        activeResolutionRequestId: null,
        resolutionPending: false,
      };
    case "map-load-failed":
      return {
        ...state,
        activeResolutionRequestId: null,
        mapUnavailable: true,
        resolution: null,
        resolutionPending: false,
      };
    case "browser-location-reason-changed":
      return { ...state, browserLocationReason: action.reason };
    case "geocoding-started":
      return { ...state, isGeocoding: true };
    case "geocoding-completed":
      return {
        ...state,
        deliveryAddress: {
          ...state.deliveryAddress,
          city: state.deliveryAddress.city || action.address.city,
          country: action.address.country || state.deliveryAddress.country,
          line1: state.deliveryAddress.line1 || action.address.line1,
          line2: state.deliveryAddress.line2 || action.address.line2,
        },
        isGeocoding: false,
      };
    case "geocoding-failed":
      return { ...state, isGeocoding: false };
    case "resolution-cleared":
      return {
        ...state,
        activeResolutionRequestId: null,
        resolution: null,
        resolutionPending: false,
      };
    case "error-recorded":
      return { ...state, error: action.error };
    case "error-cleared":
      return { ...state, error: null };
    case "resolution-started":
      return {
        ...state,
        activeResolutionRequestId: action.requestId,
        resolutionPending: true,
      };
    case "resolution-completed":
      if (action.requestId !== state.activeResolutionRequestId) {
        return state;
      }
      return {
        ...state,
        activeResolutionRequestId: null,
        resolution: action.resolution,
        resolutionPending: false,
      };
    case "resolution-failed":
      if (action.requestId !== state.activeResolutionRequestId) {
        return state;
      }
      return {
        ...state,
        activeResolutionRequestId: null,
        resolution: null,
        resolutionPending: false,
      };
  }
}

export function selectDeliveryCheckoutAddress(
  selection: DeliveryCheckoutAddressSelection,
  addresses: Array<{ id: string; isDefault: boolean }>,
): DeliveryCheckoutAddressSelection {
  if (selection.kind === "inline") {
    return selection;
  }
  return addresses.some((address) => address.id === selection.customerAddressId)
    ? selection
    : { kind: "inline" };
}

export function selectInitialDeliveryCheckoutAddress(
  addresses: Array<{ id: string; isDefault: boolean }>,
): DeliveryCheckoutAddressSelection {
  const saved = addresses.find((address) => address.isDefault) ?? addresses[0];
  return saved ? { kind: "saved", customerAddressId: saved.id } : { kind: "inline" };
}

export type DeliveryCheckoutContext = {
  usesInlineAddress: boolean;
  savedAddressId: string | null;
  payNowAvailable: boolean;
  canUsePayLater: boolean;
  itemCount: number;
  subtotal: number;
  deliveryMinimumOrder: number;
};

export type DerivedDeliveryCheckout = {
  inlineAddressValid: boolean;
  inlineBlockedByMap: boolean;
  resolutionReady: boolean;
  paymentAllowed: boolean;
  minOrderMet: boolean;
  canSubmit: boolean;
};

export function deriveDeliveryCheckout(
  state: DeliveryCheckoutState,
  context: DeliveryCheckoutContext,
): DerivedDeliveryCheckout {
  const inlineAddressValid =
    !context.usesInlineAddress ||
    (state.deliveryAddress.line1.trim().length > 0 &&
      state.deliveryAddress.lat != null &&
      state.deliveryAddress.lng != null);
  const inlineBlockedByMap =
    context.usesInlineAddress &&
    state.mapUnavailable &&
    state.deliveryAddress.lat == null &&
    state.deliveryAddress.lng == null;
  const resolutionReady =
    context.usesInlineAddress ||
    (state.resolution?.status === "verified" &&
      state.resolution.deliverable &&
      Boolean(state.resolution.token) &&
      state.resolution.customerAddressId === context.savedAddressId);
  const paymentAllowed =
    state.paymentMode === "pay_now" ? context.payNowAvailable : context.canUsePayLater;
  const minOrderMet = context.subtotal >= context.deliveryMinimumOrder;

  return {
    canSubmit:
      inlineAddressValid &&
      resolutionReady &&
      paymentAllowed &&
      context.itemCount > 0 &&
      minOrderMet,
    inlineAddressValid,
    inlineBlockedByMap,
    minOrderMet,
    paymentAllowed,
    resolutionReady,
  };
}

export function selectAvailablePaymentMode(
  paymentMode: DeliveryCheckoutPaymentMode,
  payNowAvailable: boolean,
  canUsePayLater: boolean,
): DeliveryCheckoutPaymentMode {
  if (paymentMode === "pay_now" && !payNowAvailable && canUsePayLater) {
    return "pay_later";
  }
  if (paymentMode === "pay_later" && !canUsePayLater && payNowAvailable) {
    return "pay_now";
  }
  return paymentMode;
}

export function getDeliveryPayLaterUnavailableReason(args: {
  cashOnDeliveryAvailable: boolean;
  participant: DeliveryCheckoutParticipant;
}): DeliveryCheckoutPayLaterUnavailableReason | null {
  if (!args.cashOnDeliveryAvailable) {
    return "not_offered";
  }
  if (args.participant.kind === "guest") {
    return "guest_requires_sign_in";
  }
  if (args.participant.kind === "restaurant_account") {
    return "restaurant_account";
  }
  if (!args.participant.emailVerified) {
    return "email_verification_required";
  }
  if (!args.participant.phoneVerified) {
    return "phone_verification_required";
  }
  return null;
}

export function buildDeliveryOrderInput(args: {
  state: DeliveryCheckoutState;
  orgSlug: string;
  savedAddressId: string | null;
  resolution: AddressResolutionResult;
  isAuthenticatedConsumer: boolean;
  cartLines: Array<{ productId: string; quantity: number }>;
  inlineAddressLabel?: string;
}): CreateDeliveryOrderInput {
  const input: CreateDeliveryOrderInput = {
    addressResolutionToken: args.resolution.token ?? undefined,
    contactName: args.state.contactName.trim() || null,
    contactPhone: args.state.contactPhone.trim() || null,
    items: args.cartLines.map((line) => ({
      productId: line.productId,
      quantity: line.quantity,
    })),
    notes: args.state.notes.trim() || null,
    orgSlug: args.orgSlug,
    paymentMode: args.state.paymentMode,
  };

  if (args.savedAddressId) {
    input.customerAddressId = args.savedAddressId;
    return input;
  }

  const address = args.state.deliveryAddress;
  input.deliveryAddress = {
    city: address.city.trim() || null,
    country: address.country.trim() || "UY",
    label: address.label.trim() || args.inlineAddressLabel,
    lat: address.lat,
    line1: address.line1.trim(),
    line2: address.line2.trim() || null,
    lng: address.lng,
    notes: address.notes.trim() || null,
  };
  if (args.state.saveAsCustomerAddress && args.isAuthenticatedConsumer) {
    input.saveAsCustomerAddress = true;
    input.customerAddressLabel =
      args.state.customerAddressLabel.trim() || address.label.trim() || args.inlineAddressLabel;
  }
  return input;
}

export type DeliveryCheckoutSubmitAdapters = {
  resolveInlineAddress: (
    address: DeliveryCheckoutAddress,
  ) => Promise<AddressResolutionResult | null>;
  resolveSavedAddress: (customerAddressId: string) => Promise<AddressResolutionResult | null>;
  createOrder: (input: CreateDeliveryOrderInput) => Promise<CreateDeliveryOrderResponse>;
};

export type DeliveryCheckoutSubmitResult =
  | { status: "success"; value: CreateDeliveryOrderResponse }
  | { status: "blocked"; reason: DeliveryCheckoutBlockingReason };

const REFRESHABLE_ADDRESS_REASONS: ReadonlySet<AddressResolutionReason> = new Set([
  "token_expired",
  "token_invalid",
  "address_no_longer_deliverable",
]);

function isRefreshableAddressReason(reason: unknown): reason is AddressResolutionReason {
  return (
    typeof reason === "string" && REFRESHABLE_ADDRESS_REASONS.has(reason as AddressResolutionReason)
  );
}

export async function submitDeliveryCheckout(args: {
  state: DeliveryCheckoutState;
  orgSlug: string;
  savedAddressId: string | null;
  isAuthenticatedConsumer: boolean;
  cartLines: Array<{ productId: string; quantity: number }>;
  inlineAddressLabel: string;
  adapters: DeliveryCheckoutSubmitAdapters;
}): Promise<DeliveryCheckoutSubmitResult> {
  const resolution = args.savedAddressId
    ? await args.adapters.resolveSavedAddress(args.savedAddressId)
    : await args.adapters.resolveInlineAddress(args.state.deliveryAddress);

  if (
    !resolution ||
    resolution.status !== "verified" ||
    !resolution.deliverable ||
    !resolution.token
  ) {
    return {
      status: "blocked",
      reason: resolution?.reason
        ? { kind: "address_resolution", code: resolution.reason }
        : "address_resolution_required",
    };
  }

  const input = buildDeliveryOrderInput({
    cartLines: args.cartLines,
    inlineAddressLabel: args.inlineAddressLabel,
    isAuthenticatedConsumer: args.isAuthenticatedConsumer,
    orgSlug: args.orgSlug,
    resolution,
    savedAddressId: args.savedAddressId,
    state: args.state,
  });
  try {
    const value = await args.adapters.createOrder(input);
    return { status: "success", value };
  } catch (error) {
    const reason =
      (error as { data?: { reason?: string } })?.data?.reason ??
      (error as { reason?: string })?.reason ??
      null;
    if (!isRefreshableAddressReason(reason)) {
      throw error;
    }
    if (args.savedAddressId) {
      await args.adapters.resolveSavedAddress(args.savedAddressId);
    } else {
      await args.adapters.resolveInlineAddress(args.state.deliveryAddress);
    }
    return { status: "blocked", reason: { kind: "address_resolution", code: reason } };
  }
}
